import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Redirect to /login on 401 (except for auth endpoints)
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (
      err.response?.status === 401 &&
      !err.config?.url?.includes("/api/auth/") &&
      window.location.pathname !== "/login"
    ) {
      window.location.href = "/login";
    }
    return Promise.reject(err);
  }
);

// Attach session token to all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("waimbow_session");
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

export const api = {
  // Auth
  getAuthStatus: () => axios.get(`${API}/auth/status`),
  login: (email, password, oldUserId) =>
    axios.post(`${API}/auth/login`, { email, password, old_user_id: oldUserId || null }),
  getMe: () => axios.get(`${API}/auth/me`),
  logout: () => axios.post(`${API}/auth/logout`),
  // User
  initUser: (uuid, name) => axios.post(`${API}/users/init`, { uuid, name }),
  getUser: (uuid) => axios.get(`${API}/users/${uuid}`),
  updateUser: (uuid, data) => axios.put(`${API}/users/${uuid}`, data),

  // Bucket Items
  listBucketItems: (userId, category, status) => {
    const params = new URLSearchParams({ user_id: userId });
    if (category) params.append("category", category);
    if (status) params.append("status", status);
    return axios.get(`${API}/bucket-items?${params}`);
  },
  createBucketItem: (data) => axios.post(`${API}/bucket-items`, data),
  getBucketItem: (id) => axios.get(`${API}/bucket-items/${id}`),
  updateBucketItem: (id, data) => axios.put(`${API}/bucket-items/${id}`, data),
  deleteBucketItem: (id) => axios.delete(`${API}/bucket-items/${id}`),

  // Memories
  listMemories: (userId, hasGenerated, category) => {
    const params = new URLSearchParams({ user_id: userId });
    if (hasGenerated) params.append("has_generated", "true");
    if (category) params.append("category", category);
    return axios.get(`${API}/memories?${params}`);
  },
  createMemory: (data) => axios.post(`${API}/memories`, data),
  getMemory: (id) => axios.get(`${API}/memories/${id}`),
  updateMemory: (id, data) => axios.put(`${API}/memories/${id}`, data),
  getMemoryForItem: (userId, itemId) =>
    axios.get(`${API}/memories?user_id=${userId}&bucket_item_id=${itemId}`).then(r => r.data?.[0] || null),

  // Characters
  listCharacters: (userId) => axios.get(`${API}/characters?user_id=${userId}`),
  createCharacter: (data) => axios.post(`${API}/characters`, data),
  getCharacter: (id) => axios.get(`${API}/characters/${id}`),
  updateCharacter: (id, data) => axios.put(`${API}/characters/${id}`, data),
  deleteCharacter: (id) => axios.delete(`${API}/characters/${id}`),
  addCharacterPhoto: (charId, file, userId) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", userId);
    return axios.post(`${API}/characters/${charId}/photos`, formData);
  },
  removeCharacterPhoto: (charId, photoPath) =>
    axios.delete(`${API}/characters/${charId}/photos`, {
      data: { photo_path: photoPath },
    }),

  // Upload
  uploadFile: (file, userId) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", userId);
    return axios.post(`${API}/upload`, formData);
  },

  // Generation
  generateMemoryPhoto: (data) => axios.post(`${API}/generate-memory-photo`, data),
  generateCharacterPreview: (data) =>
    axios.post(`${API}/generate-character-preview`, data),
  getGenerationQuota: (userId) =>
    axios.get(`${API}/generation-quota/${userId}`),

  // Monthly
  getMonthlyCheck: (userId) => axios.get(`${API}/monthly-check/${userId}`),

  // File URL helper
  getFileUrl: (path) => (path ? `${API}/files/${path}` : ""),
};
